/**
 * Internal Hook: useTOSMutation
 *
 * Mutation for saving TOS acceptance to backend.
 * Following the pattern from ScheduledMeetingsContext.
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { createRetryFn } from '../../../config/react-query';
import { useApiManager } from '../../../hooks';
import { useUser } from '../../UserContext';
import { useUserPermissions } from '../../UserPermissionsContext';
import { SETTINGS_QUERY_KEYS } from '../helpers';
import type { TOSAcceptance } from '../types';

export const useTOSMutation = () => {
  const apiManager = useApiManager();
  const { isSuccess: isUserPermissionsReady } = useUserPermissions();
  const { idToken } = useUser();
  const queryClient = useQueryClient();

  return useMutation<TOSAcceptance, Error, TOSAcceptance>({
    mutationFn: async (acceptance: TOSAcceptance) => {
      if (!(isUserPermissionsReady && idToken && apiManager)) {
        console.warn(
          '[TOSMutation] API Manager not initialized or no token, skipping backend save',
        );
        // Return the acceptance data to allow optimistic update
        return acceptance;
      }

      // Ensure apiManager has the latest token before making the call
      apiManager.setAuthToken(idToken);

      await apiManager.profile.storeTosAcceptance(acceptance);
      return acceptance;
    },
    onSuccess: (data) => {
      // Update the cache with new data
      queryClient.setQueryData(SETTINGS_QUERY_KEYS.tos, data);
    },
    onError: (error) => {
      console.error('[SettingsContext] Failed to save TOS acceptance:', error);
    },
    retry: createRetryFn({ maxRetries: 2 }),
  });
};
